import { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

export default function PalletMonitoring({ user, onLogout }) {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Form fields
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [colour, setColour] = useState('White');
  const [direction, setDirection] = useState('OUT');
  const [quantity, setQuantity] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  
  // New customer form
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerAddress, setNewCustomerAddress] = useState('');
  const [newCustomerEmail, setNewCustomerEmail] = useState('');

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      let allCustomers = response.data;
      
      // Filter out demo/test customers
      allCustomers = allCustomers.filter(c => {
        const name = c.business_name.toLowerCase();
        return !name.includes('demo') && 
               !name.includes('test') && 
               !name.includes('example') &&
               !name.includes('sample');
      });
      
      // Consolidate customers with same business name
      const consolidatedMap = new Map();
      
      for (const customer of allCustomers) {
        const businessName = customer.business_name.trim();
        
        if (consolidatedMap.has(businessName)) {
          continue;
        } else {
          consolidatedMap.set(businessName, customer);
        }
      }
      
      // Convert to array and sort
      const uniqueCustomers = Array.from(consolidatedMap.values()).sort((a, b) => 
        a.business_name.localeCompare(b.business_name)
      );
      
      setCustomers(uniqueCustomers);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }
    
    if (!quantity || parseInt(quantity) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    try {
      await api.post('/pallet-movements', {
        customer_id: selectedCustomer,
        date: date,
        colour: colour,
        direction: direction,
        quantity: parseInt(quantity),
        reference: reference,
        notes: notes
      });
      
      toast.success('Pallet movement recorded successfully');
      
      // Reset form
      setQuantity('');
      setReference('');
      setNotes('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record pallet movement');
    }
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    
    if (!newCustomerName || !newCustomerAddress) {
      toast.error('Please fill in business name and address');
      return;
    }

    try {
      const response = await api.post('/customers', {
        business_name: newCustomerName,
        delivery_address: newCustomerAddress,
        email: newCustomerEmail || undefined
      });
      
      toast.success('Customer created successfully');
      
      // Refresh customers list
      await fetchCustomers();
      
      // Select the new customer
      setSelectedCustomer(response.data.id);
      
      // Reset and close form
      setNewCustomerName('');
      setNewCustomerAddress('');
      setNewCustomerEmail('');
      setShowNewCustomerForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="text-center py-12">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Pallet Monitoring</h1>
          <p className="text-slate-600 mt-2">Record pallet movements (taken/returned)</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Record Pallet Movement</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="customer">Customer *</Label>
                    <Button 
                      type="button"
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowNewCustomerForm(!showNewCustomerForm)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      New Customer
                    </Button>
                  </div>
                  <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select customer..." />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.business_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="colour">Pallet Colour *</Label>
                  <Select value={colour} onValueChange={setColour}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="White">White (Free)</SelectItem>
                      <SelectItem value="Red">Red (£5)</SelectItem>
                      <SelectItem value="Blue">Blue (£5)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="direction">Direction *</Label>
                  <Select value={direction} onValueChange={setDirection}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="OUT">OUT (Taken)</SelectItem>
                      <SelectItem value="IN">IN (Returned)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="quantity">Quantity *</Label>
                  <Input
                    id="quantity"
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder="Enter quantity"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reference">Reference (e.g., Invoice #)</Label>
                  <Input
                    id="reference"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="Optional reference"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Input
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional notes"
                />
              </div>

              <Button type="submit" className="w-full md:w-auto">
                Record Movement
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* New Customer Form */}
        {showNewCustomerForm && (
          <Card className="border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle>Create New Customer</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateCustomer} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="newBusinessName">Business Name *</Label>
                    <Input
                      id="newBusinessName"
                      value={newCustomerName}
                      onChange={(e) => setNewCustomerName(e.target.value)}
                      placeholder="Enter business name"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newAddress">Delivery Address *</Label>
                    <Input
                      id="newAddress"
                      value={newCustomerAddress}
                      onChange={(e) => setNewCustomerAddress(e.target.value)}
                      placeholder="Enter delivery address"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newEmail">Email (Optional)</Label>
                    <Input
                      id="newEmail"
                      type="email"
                      value={newCustomerEmail}
                      onChange={(e) => setNewCustomerEmail(e.target.value)}
                      placeholder="customer@example.com"
                    />
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button type="submit">
                    Create Customer
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowNewCustomerForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <h3 className="font-semibold text-blue-900 mb-2">Pallet Pricing</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• White Pallets: £0 (Free)</li>
              <li>• Red Pallets: £5 per pallet</li>
              <li>• Blue Pallets: £5 per pallet</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
